import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { X, Trash2, Save, AlertTriangle, Info, CheckCircle, Search, HelpCircle } from 'lucide-react';
import ModalPortal from './ModalPortal';
import { useDispatch } from 'react-redux';
import { addNotification } from '../store/slices/uiSlice';
import { apiFetch } from '../utils/apiUtils';

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 95%;
  max-width: 1200px;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 6px;
  color: #6b7280;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
`;


const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
  cursor: help;
`;

const TooltipIcon = styled.div`
  color: #6b7280;
  transition: color 0.2s;
  
  &:hover {
    color: #374151;
  }
`;


const FieldsSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const SectionTitleWithSearch = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InlineSearchContainer = styled.div`
  position: relative;
  width: 300px;
`;

const InlineSearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const InlineSearchIcon = styled.div`
  position: absolute;
  left: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
`;

const FieldsList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
`;

const FieldItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  transition: all 0.2s;
  min-height: auto;
  
  &:hover {
    border-color: #d1d5db;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const FieldInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const FieldName = styled.div`
  font-weight: 500;
  color: #374151;
  font-size: 0.8rem;
`;


const PreserveCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: auto;
`;

const CheckboxInput = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #000000;
`;

const CheckboxLabel = styled.label`
  font-size: 0.75rem;
  color: #374151;
  cursor: pointer;
  user-select: none;
`;

const SummarySection = styled.div`
  position: sticky;
  bottom: 0;
  background: white;
  border-top: 1px solid #e5e7eb;
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;


const SummaryStats = styled.div`
  display: flex;
  gap: 2rem;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  background: white;
`;

const FooterSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${props => props.variant === 'primary' ? `
    background: #ef4444;
    color: white;
    border: 1px solid #ef4444;
    
    &:hover {
      background: #dc2626;
      border-color: #dc2626;
    }
    
    &:disabled {
      background: #fca5a5;
      border-color: #fca5a5;
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #f3f4f6;
  border-top: 2px solid #6b7280;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const WarningSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const WarningIcon = styled.div`
  color: #f59e0b;
  flex-shrink: 0;
`;

const WarningText = styled.div`
  color: #92400e;
  font-size: 0.875rem;
  line-height: 1.4;
`;


// Main Component
const FieldDeletionModal = ({ isOpen, onClose, filename, onConfirm }) => {
  const dispatch = useDispatch();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [fieldChoices, setFieldChoices] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [protectionInfo, setProtectionInfo] = useState(null);

  // Fetch analysis when modal opens
  const fetchAnalysis = useCallback(async () => {
    if (!filename) return;
    
    setLoading(true);
    try {
      const response = await apiFetch(`/system-files/delete-analysis/${encodeURIComponent(filename)}`);
      const data = await response.json();
      
      if (data.success) {
        setAnalysis(data);
        // Initialize field choices - all fields start as "preserve" (true)
        const initialChoices = {};
        [...(data.fields_only_in_file || []), ...(data.fields_in_multiple_files || [])].forEach(field => {
          initialChoices[field.fieldKey] = true; // true = preserve, false = delete
        });
        setFieldChoices(initialChoices);
      } else {
        console.error('Failed to fetch analysis:', data.message);
        dispatch(addNotification({
          type: 'error',
          title: 'Analysis Failed',
          message: `Failed to analyze file ${filename}: ${data.message || 'Unknown error'}`
        }));
      }
    } catch (error) {
      console.error('Error fetching analysis:', error);
      dispatch(addNotification({
        type: 'error',
        title: 'Analysis Failed',
        message: `Failed to analyze file ${filename}: ${error.message || 'Network error'}`
      }));
    } finally {
      setLoading(false);
    }
  }, [filename, dispatch]);

  // Fetch protection info when modal opens
  const fetchProtectionInfo = useCallback(async () => {
    if (!filename) return;
    
    try {
      const response = await apiFetch('/system-files/check-file-protection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filename })
      });
      const data = await response.json();
      
      if (data.success) {
        setProtectionInfo(data);
      }
    } catch (error) {
      console.error('Error checking file protection:', error);
    }
  }, [filename]);

  useEffect(() => {
    if (isOpen && filename) {
      fetchAnalysis();
      fetchProtectionInfo();
    }
  }, [isOpen, filename, fetchAnalysis, fetchProtectionInfo]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAnalysis(null);
      setFieldChoices({});
      setLoading(false);
      setDeleting(false);
      setSearchTerm('');
      setProtectionInfo(null);
    }
  }, [isOpen]);

  const handleFieldChoice = useCallback((fieldKey, preserve) => {
    setFieldChoices(prev => ({
      ...prev,
      [fieldKey]: preserve
    }));
  }, []);

  const handleDelete = async () => {
    if (!analysis) return;
    
    setDeleting(true);
    try {
      const deleteFields = [];
      const convertToManual = [];
      
      // Process field choices
      Object.entries(fieldChoices).forEach(([fieldKey, preserve]) => {
        // Find the original field data to get record_id and field_name
        const allFields = [...processedFields.onlyInFile, ...processedFields.inMultipleFiles];
        const field = allFields.find(f => f.fieldKey === fieldKey);
        
        if (field) {
          const fieldData = {
            record_id: field.record_id,
            field_name: field.field_name
          };
          
          if (preserve) {
            convertToManual.push(fieldData);
          } else {
            deleteFields.push(fieldData);
          }
        }
      });
      
      const response = await apiFetch('/system-files/delete-with-options', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename,
          delete_fields: deleteFields,
          convert_to_manual: convertToManual
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        dispatch(addNotification({
          type: 'success',
          title: 'File Deleted Successfully',
          message: `Successfully deleted ${filename} and processed ${result.fields_deleted} fields`
        }));
        onConfirm();
        onClose();
      } else {
        const errorMessage = result.message || result.error || 'Unknown error occurred';
        console.error('Delete failed:', errorMessage);
        console.error('Full response:', result);
        
        dispatch(addNotification({
          type: 'error',
          title: 'Delete Failed',
          message: `Failed to delete ${filename}: ${errorMessage}`
        }));
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      
      dispatch(addNotification({
        type: 'error',
        title: 'Delete Failed',
        message: `Failed to delete ${filename}: ${error.message || 'Network error'}`
      }));
    } finally {
      setDeleting(false);
    }
  };

  const processedFields = useMemo(() => {
    if (!analysis) return { onlyInFile: [], inMultipleFiles: [] };
    
    // Deduplicate fields by field_name to prevent duplicates
    const deduplicateFields = (fields) => {
      const seen = new Set();
      return fields.filter(field => {
        const key = field.field_name;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
    };
    
    const onlyInFile = deduplicateFields(analysis.fields_only_in_file || []);
    const inMultipleFiles = deduplicateFields(analysis.fields_in_multiple_files || []);
    
    // Filter by search term
    const filterFields = (fields) => {
      if (!searchTerm.trim()) return fields;
      const term = searchTerm.toLowerCase();
      return fields.filter(field => {
        const fieldName = field.field_name?.toLowerCase() || '';
        const sampleValue = (field.sample_value || field.field_value || field.value || '').toString().toLowerCase();
        return fieldName.includes(term) || sampleValue.includes(term);
      });
    };
    
    return {
      onlyInFile: filterFields(onlyInFile).map((field, index) => ({
        ...field,
        fieldKey: `only_${field.field_name}_${index}`
      })),
      inMultipleFiles: filterFields(inMultipleFiles).map((field, index) => ({
        ...field,
        fieldKey: `multiple_${field.field_name}_${index}`
      }))
    };
  }, [analysis, searchTerm]);

  if (!isOpen) return null;

  return (
    <ModalPortal isOpen={isOpen}>
      <ModalOverlay>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              <div style={{
                width: '32px',
                height: '32px',
                background: '#ef4444',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '0.75rem'
              }}>
                <Trash2 size={16} color="white" />
              </div>
              {filename}
            </ModalTitle>
            <CloseButton onClick={onClose}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>
          
          <ModalBody>
            <WarningSection>
              <WarningIcon>
                <AlertTriangle size={20} />
              </WarningIcon>
              <WarningText>
                <strong>Warning:</strong> This will permanently delete the file and all its data. 
                You can choose to keep specific fields if needed.
              </WarningText>
            </WarningSection>

            {protectionInfo && protectionInfo.protected_count > 0 && (
              <WarningSection style={{ 
                background: '#dbeafe', 
                borderColor: '#3b82f6' 
              }}>
                <WarningIcon style={{ color: '#3b82f6' }}>
                  <Info size={20} />
                </WarningIcon>
                <WarningText style={{ color: '#1e3a8a' }}>
                  <strong>Protected Loans:</strong> {protectionInfo.protected_count} loan(s) in this file have active user assignments and will be <strong>archived</strong> instead of deleted. 
                  {protectionInfo.protected_loans.length <= 5 && (
                    <>
                      <br />
                      Loans: {protectionInfo.protected_loans.map(loan => loan.loan_id).join(', ')}
                    </>
                  )}
                </WarningText>
              </WarningSection>
            )}

            {loading ? (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '200px',
                color: '#6b7280'
              }}>
                <LoadingSpinner />
                <span style={{ marginLeft: '1rem' }}>Analyzing file deletion impact...</span>
              </div>
            ) : analysis ? (
              <>
                {processedFields.onlyInFile.length > 0 && (
                  <FieldsSection>
                    <SectionHeader>
                      <SectionTitleWithSearch>
                        <Info size={16} />
                        Fields That Will Be Removed ({processedFields.onlyInFile.length})
                      </SectionTitleWithSearch>
                      <InlineSearchContainer>
                        <InlineSearchIcon>
                          <Search size={14} />
                        </InlineSearchIcon>
                        <InlineSearchInput
                          type="text"
                          placeholder="Search fields..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InlineSearchContainer>
                    </SectionHeader>
                    <FieldsList>
                      {processedFields.onlyInFile.map(field => (
                        <FieldItem key={field.fieldKey}>
                          <FieldInfo>
                            <FieldName>{field.field_name}</FieldName>
                            <TooltipContainer title={`Sample value: ${field.sample_value || field.field_value || field.value || 'No sample value'}`}>
                              <TooltipIcon>
                                <HelpCircle size={14} />
                              </TooltipIcon>
                            </TooltipContainer>
                          </FieldInfo>
                          <PreserveCheckbox>
                            <CheckboxInput
                              type="checkbox"
                              checked={fieldChoices[field.fieldKey] || false}
                              onChange={(e) => handleFieldChoice(field.fieldKey, e.target.checked)}
                            />
                            <CheckboxLabel>Keep field</CheckboxLabel>
                          </PreserveCheckbox>
                        </FieldItem>
                      ))}
                    </FieldsList>
                  </FieldsSection>
                )}

                {processedFields.inMultipleFiles.length > 0 && (
                  <FieldsSection>
                    <SectionTitle>
                      <CheckCircle size={16} />
                      Fields in Multiple Files ({processedFields.inMultipleFiles.length})
                    </SectionTitle>
                    <FieldsList>
                      {processedFields.inMultipleFiles.map(field => (
                        <FieldItem key={field.fieldKey}>
                          <FieldInfo>
                            <FieldName>{field.field_name}</FieldName>
                            <TooltipContainer title={`Sample value: ${field.sample_value || field.field_value || field.value || 'No sample value'}`}>
                              <TooltipIcon>
                                <HelpCircle size={14} />
                              </TooltipIcon>
                            </TooltipContainer>
                          </FieldInfo>
                          <PreserveCheckbox>
                            <CheckboxInput
                              type="checkbox"
                              checked={fieldChoices[field.fieldKey] || false}
                              onChange={(e) => handleFieldChoice(field.fieldKey, e.target.checked)}
                            />
                            <CheckboxLabel>Keep field</CheckboxLabel>
                          </PreserveCheckbox>
                        </FieldItem>
                      ))}
                    </FieldsList>
                  </FieldsSection>
                )}
              </>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '2rem', 
                color: '#6b7280' 
              }}>
                Failed to load analysis
              </div>
            )}
          </ModalBody>


          <ModalFooter>
            {analysis && (
              <FooterSummary>
                <SummaryStats>
                  <StatItem>
                    <StatValue>{processedFields.onlyInFile.length + processedFields.inMultipleFiles.length}</StatValue>
                    <StatLabel>Total Fields</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatValue>{Object.values(fieldChoices).filter(Boolean).length}</StatValue>
                    <StatLabel>Will Keep</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatValue>{Object.values(fieldChoices).filter(v => !v).length}</StatValue>
                    <StatLabel>Will Remove</StatLabel>
                  </StatItem>
                </SummaryStats>
              </FooterSummary>
            )}
            
            <FooterButtons>
              <Button onClick={onClose}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleDelete}
                disabled={deleting || loading}
              >
                {deleting ? (
                  <>
                    <LoadingSpinner />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete File
                  </>
                )}
              </Button>
            </FooterButtons>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default FieldDeletionModal;